/** Documents — list view (server-side pagination/sort/search + delete). */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useTable } from "@refinedev/react-table";
import {
  useGo,
  useNavigation,
  useDelete,
  type HttpError,
} from "@refinedev/core";
import {
  ExternalLink as ExternalLinkIcon,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from "@/components/ui/icon";
import type { Document } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";

import {
  DataTable,
  PageHeader,
  SearchBox,
  useSearchFilter,
  type RefineTableCore,
} from "../projects/_shared";
import { humanize } from "../projects/_form";

export function DocumentList() {
  const go = useGo();
  const { editUrl, showUrl, createUrl } = useNavigation();
  const { mutate: deleteDocument } = useDelete();
  const [pendingDelete, setPendingDelete] = React.useState<Document | null>(
    null,
  );

  const columns = useMemo<ColumnDef<Document, unknown>[]>(
    () => [
      { id: "title", accessorKey: "title", header: "Title" },
      {
        id: "source_system",
        accessorKey: "source_system",
        header: "Source",
        cell: ({ row }) => (
          <Badge variant="outline">{row.original.source_system}</Badge>
        ),
      },
      {
        id: "confidentiality",
        accessorKey: "confidentiality",
        header: "Confidentiality",
        cell: ({ row }) => (
          <Badge
            variant={
              row.original.confidentiality === "confidential"
                ? "secondary"
                : "outline"
            }
          >
            {humanize(row.original.confidentiality)}
          </Badge>
        ),
      },
      {
        id: "external_url",
        accessorKey: "external_url",
        header: "Link",
        enableSorting: false,
        cell: ({ row }) =>
          row.original.external_url ? (
            <a
              href={row.original.external_url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLinkIcon className="h-3.5 w-3.5" /> Open
            </a>
          ) : (
            "—"
          ),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) =>
          new Date(row.original.created_at).toLocaleDateString(),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onEdit={() => go({ to: editUrl("documents", row.original.id) })}
            onShow={() => go({ to: showUrl("documents", row.original.id) })}
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, editUrl, showUrl],
  );

  const table = useTable<Document, HttpError>({
    columns,
    refineCoreProps: {
      resource: "documents",
      pagination: { pageSize: 25 },
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
    },
  });

  const core = table.refineCore as unknown as RefineTableCore & {
    setFilters: (
      filters: { field: string; operator: "contains"; value: string }[],
    ) => void;
  };
  const [search, setSearch] = useSearchFilter(core.setFilters);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Documents"
        description="External documents registered in the operating core."
        icon="documents"
        action={
          <Button size="sm" onClick={() => go({ to: createUrl("documents") })}>
            <Plus className="mr-2 h-4 w-4" /> New Document
          </Button>
        }
      />

      <SearchBox
        value={search}
        onChange={setSearch}
        placeholder="Search documents…"
      />

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage="No documents registered yet."
        onRowClick={(d) => go({ to: showUrl("documents", d.id) })}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open: boolean) => !open && setPendingDelete(null)}
        title="Delete document?"
        description={
          <>
            This removes{" "}
            <span className="font-medium">{pendingDelete?.title}</span> from the
            registry. This action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingDelete) {
            deleteDocument({ resource: "documents", id: pendingDelete.id });
          }
        }}
      />
    </div>
  );
}

function RowActions({
  onEdit,
  onShow,
  onDelete,
}: {
  onEdit: () => void;
  onShow: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onShow}>View</DropdownMenuItem>
        <DropdownMenuItem onClick={onEdit}>
          <Pencil className="mr-2 h-4 w-4" /> Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="mr-2 h-4 w-4" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default DocumentList;
